/**
 * Small pieces the stock screens share: the item picker (code, name or a
 * scanned barcode -- a carton's barcode picks the carton), a labelled
 * field, a store selector and the cancel-with-a-reason dialog.
 */
import { useRef, useState, type ReactNode } from 'react'
import { useMutation } from '@tanstack/react-query'
import {
  Alert, Autocomplete, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  MenuItem, TextField, Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'

import { errorMessage } from '../api/client'
import type { InvItem, InvItemRef, InvStore } from '../api/types'
import { c } from '../theme'
import { fmtQty } from './qty'

/** Every unit an item can be counted in, base unit first. */
export const unitsOf = (it: InvItem | null) =>
  it ? [it.base_unit, ...it.units.map((u) => u.unit)] : []

/** "10 CTN + 4 PCS" from a report row (which carries only its biggest pack). */
export function packsOf(qty: string | number, r: Pick<InvItemRef, 'base_unit' | 'pack_unit'
  | 'pack_factor'>): string {
  const n = Number(qty)
  const f = Number(r.pack_factor ?? 0)
  if (!r.pack_unit || !f || !Number.isFinite(n) || Math.abs(n) < f) {
    return `${fmtQty(n)} ${r.base_unit}`
  }
  const sign = n < 0 ? '−' : ''
  const a = Math.abs(n)
  const packs = Math.floor(a / f)
  const rest = +(a - packs * f).toFixed(3)
  return `${sign}${fmtQty(packs)} ${r.pack_unit}${rest ? ` + ${fmtQty(rest)} ${r.base_unit}` : ''}`
}

export function ItemPicker({ options, value, storeId, onPick, inputRef, label, autoFocus }: {
  options: InvItem[]
  value: InvItem | null
  /** The store whose stock is shown beside each item. */
  storeId?: number | ''
  onPick: (it: InvItem | null, unit?: string) => void
  inputRef?: (el: HTMLInputElement | null) => void
  label: string
  autoFocus?: boolean
}) {
  const { t, i18n } = useTranslation()
  const ar = i18n.language === 'ar'
  const typed = useRef('')
  const held = (o: InvItem) => (storeId ? o.stock[String(storeId)] ?? '0' : o.on_hand)
  return (
    <Autocomplete<InvItem> size="small" options={options} value={value}
      autoHighlight openOnFocus={false}
      getOptionLabel={(o) => `${o.code} — ${(ar && o.name_ar) || o.name_en}`}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      onInputChange={(_, v) => { typed.current = v }}
      filterOptions={(opts, s) => {
        const n = s.inputValue.trim().toLowerCase()
        if (!n) return opts.slice(0, 50)
        return opts.filter((o) => o.code.toLowerCase().includes(n)
          || o.name_en.toLowerCase().includes(n) || (o.name_ar ?? '').includes(s.inputValue.trim())
          || o.barcode === n || o.units.some((u) => u.barcode === n)).slice(0, 50)
      }}
      onChange={(_, v) => {
        const scanned = typed.current.trim()
        onPick(v, v?.units.find((u) => u.barcode && u.barcode === scanned)?.unit)
      }}
      renderOption={(props, o) => (
        <li {...props} key={o.id}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontSize: 14 }} noWrap>
              <span className="doc-no">{o.code}</span> · {(ar && o.name_ar) || o.name_en}</Typography>
            <Typography sx={{ fontSize: 12, color: c.inkMuted }}>
              {t('inv.inThisStore', { qty: fmtQty(held(o)), unit: o.base_unit })}
            </Typography>
          </Box>
        </li>
      )}
      renderInput={(pr) => <TextField {...pr} placeholder={t('inv.pickItem')} autoFocus={autoFocus}
        inputRef={inputRef} inputProps={{ ...pr.inputProps, 'aria-label': label }} />} />
  )
}

export function StoreSelect({ label, value, stores, onChange, exclude, sx, allowAll }: {
  label: string
  value: number | ''
  stores: InvStore[]
  onChange: (id: number | '') => void
  exclude?: number | ''
  sx?: object
  /** Offer "All stores" as the empty choice. */
  allowAll?: boolean
}) {
  const { t, i18n } = useTranslation()
  const ar = i18n.language === 'ar'
  return (
    <TextField select size="small" label={label} value={value} sx={sx}
      SelectProps={{ displayEmpty: !!allowAll }} InputLabelProps={allowAll ? { shrink: true } : undefined}
      onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}>
      {allowAll && <MenuItem value="">{t('stock.allStores')}</MenuItem>}
      {stores.filter((s) => s.is_active && s.id !== exclude).map((s) => (
        <MenuItem key={s.id} value={s.id}>
          {(ar && s.name_ar) || s.name_en}
          {s.kind === 'VAN' && <Typography component="span"
            sx={{ ml: 1, fontSize: 11.5, color: c.inkMuted }}>{t('inv.kindVAN')}</Typography>}
        </MenuItem>))}
    </TextField>
  )
}

export function Field({ label, value, sub }: { label: string; value: ReactNode; sub?: string }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 12, color: c.inkMuted }}>{label}</Typography>
      <Typography sx={{ fontSize: 15, fontWeight: 500 }} dir="auto">{value}</Typography>
      {sub && <Typography className="doc-no" sx={{ fontSize: 12, color: c.inkMuted }}>{sub}</Typography>}
    </Box>
  )
}

export function CancelDialog({ open, title, body, onClose, onDone, run }: {
  open: boolean
  title: string
  body: string
  onClose: () => void
  onDone: () => void
  run: (reason: string) => Promise<unknown>
}) {
  const { t } = useTranslation()
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)
  const go = useMutation({
    mutationFn: () => run(reason.trim()),
    onSuccess: () => { setReason(''); setError(null); onDone() },
    onError: (e) => setError(errorMessage(e)),
  })
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Typography sx={{ fontSize: 14, mb: 2 }}>{body}</Typography>
        <TextField autoFocus fullWidth label={t('inv.cancelReason')} value={reason}
          onChange={(e) => setReason(e.target.value)} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button color="inherit" onClick={onClose}>{t('common.close')}</Button>
        <Button color="error" variant="contained" disabled={reason.trim().length < 3 || go.isPending}
          onClick={() => go.mutate()}>{t('inv.cancelConfirm')}</Button>
      </DialogActions>
    </Dialog>
  )
}
